/**
 * Acciones de navegación de la app: un solo lugar para push/replace/back
 * (evita mezclar history.back con rutas MLV en distintos presenters).
 */

import { mlvScrollToTop } from './mlvScrollTop.js';
import { MLV_Z } from '../ui/mlvZLayers.js';
import { SS_GUEST_EXPLORE } from '../sessionKeys.js';

const MLV_STATE_KEY = 'mlv';

function readMlvState() {
  if (typeof window === 'undefined') return null;
  return window.history.state?.[MLV_STATE_KEY] ?? null;
}

/** Pestañas principales (perfil / ventas / publicar / admin): se reemplazan entre sí sin apilar. */
function isShellTabState(s) {
  if (!s || typeof s !== 'object') return false;
  if (s.t === 'profile') return true;
  if (s.t === 'admin') return true;
  if (s.t === 'vendedor' && (s.mode === 'mis' || s.mode === 'publicar')) return true;
  return false;
}

export function createMlvNavigation({
  getFirebase,
  mlvHist,
  catalogoPublicoPresenter,
  perfilVentasPresenter,
  productoFeedVerticalPresenter,
  vendedorPresenter,
  vendedorRepository,
  vendedorExperienciaInfoPresenter,
  premiumPlanPresenter,
  showCatalogoHomePlain,
  /** Siempre la vista actual (evita capturar el stub previo a createAuthPresenter). */
  getLoginView,
  showProfileView,
  showAdminPanelView,
  isProfileComplete,
  serviciosPresenters = null,
}) {
  function goBack() {
    window.history.back();
  }

  function replaceCatalog() {
    mlvScrollToTop();
    mlvHist.replace({ t: 'catalog' });
    showCatalogoHomePlain();
  }

  /** Solo “Explorar como invitado” desde login: apila catálogo para que atrás vuelva al login. */
  function pushCatalogGuestEntry() {
    try {
      sessionStorage.setItem(SS_GUEST_EXPLORE, '1');
    } catch (_e) {
      /* ignore */
    }
    mlvScrollToTop();
    mlvHist.push({ t: 'catalog' });
    showCatalogoHomePlain();
  }

  function pushLoginShow() {
    mlvScrollToTop();
    mlvHist.push({ t: 'login' });
    getLoginView?.()();
  }

  function replaceLoginShow() {
    mlvScrollToTop();
    mlvHist.replace({ t: 'login' });
    getLoginView?.()();
  }

  function pushProfileShow() {
    mlvScrollToTop();
    mlvHist.push({ t: 'profile' });
    showProfileView();
  }

  /** Perfil: desde catálogo/producto se apila; entre pestañas del shell reemplaza la entrada actual. */
  function goToProfile() {
    mlvScrollToTop();
    const cur = readMlvState();
    if (isShellTabState(cur)) {
      mlvHist.replace({ t: 'profile' });
      showProfileView();
      return;
    }
    pushProfileShow();
  }

  /** Publicar: mismo criterio que goToProfile para no acumular [perfil, ventas, publicar, …]. */
  function goToVendedorPublicar() {
    mlvScrollToTop();
    const cur = readMlvState();
    if (isShellTabState(cur)) {
      mlvHist.replace({ t: 'vendedor', mode: 'publicar' });
      return;
    }
    mlvHist.push({ t: 'vendedor', mode: 'publicar' });
  }

  /**
   * Barra inferior en Mis publicaciones: si el historial es `mis`, atrás vuelve al padre (perfil o catálogo).
   * Si `mis` se abrió encima de publicar sin push, va a perfil reemplazando.
   */
  function handleMisBottomNavPerfil() {
    const cur = readMlvState();
    if (cur?.t === 'vendedor' && cur.mode === 'mis') {
      goBack();
      return;
    }
    goToProfile();
  }

  function pushAdminShow() {
    mlvScrollToTop();
    mlvHist.push({ t: 'admin' });
    showAdminPanelView();
  }

  function pushProductDetail(id) {
    mlvScrollToTop();
    mlvHist.push({ t: 'product', id });
    catalogoPublicoPresenter.showProductoDetalle(id, buildProductoDetalleOpts());
  }

  function pushPerfilVendedorUid(uid) {
    const u = String(uid || '').trim();
    if (!/^[A-Za-z0-9_-]{10,128}$/.test(u)) return;
    mlvScrollToTop();
    mlvHist.push({ t: 'perfilVendedor', uid: u });
    void perfilVentasPresenter.show({
      vendedorUid: u,
      onBack: goBack,
      onOpenProducto: (id) => pushProductDetail(id),
    });
  }

  function buildFeedOpts() {
    return {
      onBack: goBack,
      onVerDescripcion: (id) => pushProductDetail(id),
    };
  }

  async function pushFeedVertical(categoriaId, startProductId) {
    mlvScrollToTop();
    mlvHist.push({
      t: 'feed',
      categoriaId: categoriaId || 'general',
      startId: startProductId,
    });
    await productoFeedVerticalPresenter.showFeed({
      categoriaId: categoriaId || 'general',
      startProductId,
      isAuthenticated: Boolean(getFirebase().auth.currentUser),
      ...buildFeedOpts(),
    });
  }

  function buildProductoDetalleOpts() {
    return {
      isAuthenticated: Boolean(getFirebase().auth.currentUser),
      onBack: goBack,
      onOpenProfile: goToProfile,
      onSellProduct: () => openSellOrOnboarding(),
      onBackToLogin: pushLoginShow,
      onVerPlanPremium: () => void pushPremiumPlan(),
      onOpenPerfilVendedorUid: (uid) => pushPerfilVendedorUid(uid),
    };
  }

  function misPublicacionesBackOpts() {
    return { onBack: goBack };
  }

  function pushVendedorDatos() {
    mlvScrollToTop();
    mlvHist.push({ t: 'vendedor', mode: 'datos' });
  }

  function pushVendedorCambiarTelefono() {
    mlvScrollToTop();
    mlvHist.push({ t: 'vendedor', mode: 'telefono' });
  }

  function replaceVendedorDatos() {
    mlvScrollToTop();
    mlvHist.replace({ t: 'vendedor', mode: 'datos' });
  }

  function pushVendedorMis() {
    mlvScrollToTop();
    mlvHist.push({ t: 'vendedor', mode: 'mis' });
  }

  function pushVendedorPublicar() {
    mlvScrollToTop();
    mlvHist.push({ t: 'vendedor', mode: 'publicar' });
  }

  function pushVendedorExperienciaInfo() {
    mlvScrollToTop();
    mlvHist.push({ t: 'vendedorInfo' });
    vendedorExperienciaInfoPresenter.show({ onBack: goBack });
  }

  function pushServiciosHome() {
    mlvScrollToTop();
    mlvHist.push({ t: 'servicios', mode: 'home' });
    void serviciosPresenters?.showServiciosHome?.();
  }

  function replaceServiciosHome() {
    mlvScrollToTop();
    mlvHist.replace({ t: 'servicios', mode: 'home' });
    void serviciosPresenters?.showServiciosHome?.();
  }

  function pushServiciosMiPerfil() {
    mlvScrollToTop();
    mlvHist.push({ t: 'servicios', mode: 'miPerfil' });
    void serviciosPresenters?.showServiciosMiPerfil?.();
  }

  function pushServiciosFicha(uid) {
    const u = String(uid || '').trim();
    if (!/^[A-Za-z0-9_-]{10,128}$/.test(u)) return;
    mlvScrollToTop();
    mlvHist.push({ t: 'servicios', mode: 'ficha', uid: u });
    void serviciosPresenters?.showServiciosFicha?.(u);
  }

  async function pushPremiumPlan() {
    mlvScrollToTop();
    mlvHist.push({ t: 'premiumPlan' });
    const user = getFirebase().auth.currentUser;
    if (!user) {
      pushLoginShow();
      return;
    }
    try {
      const p = await vendedorRepository.syncUserProfile(user.uid, user.email);
      premiumPlanPresenter.show({ onBack: goBack, profile: p, user });
    } catch (e) {
      console.error(e);
      goBack();
    }
  }

  async function openSellOrOnboarding() {
    mlvScrollToTop();
    const user = getFirebase().auth.currentUser;
    if (!user) {
      pushLoginShow();
      return;
    }
    try {
      const profile = await vendedorRepository.syncUserProfile(user.uid, user.email);
      if (!profile || !isProfileComplete(profile)) {
        pushVendedorDatos();
        vendedorPresenter.datosVendedorView(profile);
        return;
      }
      goToVendedorPublicar();
      await vendedorPresenter.showPublicarForm();
    } catch (e) {
      console.error(e);
      const msg = e?.message || 'No se pudo abrir publicar. Reintentá.';
      const errEl = document.createElement('div');
      errEl.className =
        'fixed bottom-4 left-4 right-4 rounded-2xl border border-red-200 bg-red-50/95 px-4 py-3 text-sm text-red-900 shadow-lg';
      errEl.style.zIndex = String(MLV_Z.navErrorToast);
      errEl.textContent = msg;
      document.body.appendChild(errEl);
      setTimeout(() => errEl.remove(), 5000);
    }
  }

  return {
    goBack,
    replaceCatalog,
    pushCatalogGuestEntry,
    pushLoginShow,
    replaceLoginShow,
    pushProfileShow,
    goToProfile,
    goToVendedorPublicar,
    handleMisBottomNavPerfil,
    pushAdminShow,
    pushProductDetail,
    pushPerfilVendedorUid,
    pushFeedVertical,
    buildFeedOpts,
    buildProductoDetalleOpts,
    misPublicacionesBackOpts,
    pushVendedorDatos,
    pushVendedorCambiarTelefono,
    replaceVendedorDatos,
    pushVendedorMis,
    pushVendedorPublicar,
    pushVendedorExperienciaInfo,
    pushPremiumPlan,
    openSellOrOnboarding,
    pushServiciosHome,
    replaceServiciosHome,
    pushServiciosMiPerfil,
    pushServiciosFicha,
  };
}
